import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

import { api } from "../api";
import type { SessionUser } from "../types";

type Audience = "student" | "teacher" | "visitor" | null;

type AuthValue = {
  user: SessionUser | null;
  audience: Audience;
  loading: boolean;
  login: (role: "student" | "teacher", identifier: string, password: string, remember: boolean) => Promise<SessionUser>;
  logout: () => Promise<void>;
  enterAsVisitor: () => void;
};

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [audience, setAudience] = useState<Audience>(() => sessionStorage.getItem("cxz-audience") as Audience);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.session().then((session) => {
      setUser(session);
      setAudience(session.role);
      sessionStorage.setItem("cxz-audience", session.role);
    }).catch(() => undefined).finally(() => setLoading(false));
  }, []);

  const value = useMemo<AuthValue>(() => ({
    user,
    audience,
    loading,
    async login(role, identifier, password, remember) {
      const session = await api.login({ role, identifier, password, remember });
      setUser(session);
      setAudience(role);
      sessionStorage.setItem("cxz-audience", role);
      return session;
    },
    async logout() {
      await api.logout().catch(() => undefined);
      setUser(null);
      setAudience(null);
      Object.keys(sessionStorage).filter((key) => key.startsWith("cxz-project-coach:")).forEach((key) => sessionStorage.removeItem(key));
      sessionStorage.removeItem("cxz-audience");
      window.location.replace("/");
    },
    enterAsVisitor() {
      setUser(null);
      setAudience("visitor");
      sessionStorage.setItem("cxz-audience", "visitor");
    },
  }), [audience, loading, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook and provider intentionally share this module so authentication state has one source.
// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
